// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import {Avatar} from '@app/features/ui/components/Avatar';
import {UserProfilePopout} from '@app/features/user/components/popouts/UserProfilePopout';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import styles from './DMProfilePanel.module.css';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

const keepSidebarOpen = () => {};

/** Shares the existing profile renderer, privacy rules and asynchronous profile loader. */
export const DMProfilePanel = observer(({channel}: {channel: Channel}) => {
	const {t} = useLingui();
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const isGroup = channel.type === ChannelTypes.GROUP_DM;
	const currentUserId = Users.getCurrentUser()?.id;
	const memberIds = isGroup && currentUserId ? [...new Set([...channel.recipientIds, currentUserId])] : channel.recipientIds;
	const participants = memberIds.map((id) => Users.getUser(id)).filter((user) => user != null);
	const selectedUser = participants.find((user) => user.id === selectedId) ?? participants[0];
	return (
		<aside className={styles.panel} aria-label={isGroup ? t`Group profiles` : t`Conversation profile`} data-flx="channel.dm-profile-panel">
			{isGroup && (
				<div className={styles.participants}>
					<h2><Trans>Members</Trans> <span>{participants.length}</span></h2>
					<div className={styles.memberList}>
						{participants.map((user) => (
							<button key={user.id} type="button" className={styles.member} aria-pressed={user.id === selectedUser?.id} onClick={() => setSelectedId(user.id)}>
								<Avatar user={user} size={28} />
								<span>{NicknameUtils.getNickname(user, undefined, channel.id)}</span>
							</button>
						))}
					</div>
				</div>
			)}
			<div className={styles.profile}>
				{selectedUser ? (
					<UserProfilePopout key={selectedUser.id} popoutKey={`dm-sidebar:${channel.id}:${selectedUser.id}`} user={selectedUser} isWebhook={false} inlineSidebar onClose={keepSidebarOpen} />
				) : <p className={styles.empty}><Trans>Select a member to view their profile.</Trans></p>}
			</div>
		</aside>
	);
});
